/**
 * Stable error codes returned in `error.code`.
 *
 * Codes are part of the public API contract: clients may branch on them, so
 * existing codes must not be renamed or repurposed. Domain-specific codes are
 * added by the phase that introduces the corresponding module.
 */
export const API_ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  CONFLICT: 'CONFLICT',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNPROCESSABLE_ENTITY: 'UNPROCESSABLE_ENTITY',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // --- Identity and authentication (Phase 1) ------------------------------
  /** Registration hit the unique email constraint. */
  EMAIL_ALREADY_REGISTERED: 'EMAIL_ALREADY_REGISTERED',
  /**
   * Returned for a wrong password, an unknown address and a malformed pair
   * alike, so a caller cannot use the response to discover which addresses
   * exist.
   */
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_DEACTIVATED: 'ACCOUNT_DEACTIVATED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  /** The submitted password does not satisfy the configured policy. */
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  /** Covers unknown, expired, superseded and already-redeemed alike. */
  INVALID_VERIFICATION_TOKEN: 'INVALID_VERIFICATION_TOKEN',
  INVALID_RESET_TOKEN: 'INVALID_RESET_TOKEN',
  /** Missing, expired, revoked or replayed refresh token. */
  INVALID_REFRESH_TOKEN: 'INVALID_REFRESH_TOKEN',

  // --- Administration guardrails ------------------------------------------
  /** A role marked `isSystem` cannot be deleted or have its code changed. */
  PROTECTED_SYSTEM_ROLE: 'PROTECTED_SYSTEM_ROLE',
  /** Refuses the change that would leave no active super administrator. */
  LAST_SUPER_ADMIN: 'LAST_SUPER_ADMIN',
  /** A path or body parameter was not a canonical UUID. */
  INVALID_UUID: 'INVALID_UUID',

  // --- Customer financing applications -----------------------------------
  /** Another non-terminal financing journey already exists for the customer. */
  APPLICATION_ACTIVE_JOURNEY_EXISTS: 'APPLICATION_ACTIVE_JOURNEY_EXISTS',
  /** A non-zero application discount has not been explicitly authorized by staff. */
  APPLICATION_DISCOUNT_REVIEW_REQUIRED: 'APPLICATION_DISCOUNT_REVIEW_REQUIRED',

  // --- Mandates and payment provider (Phase 3) ---------------------------
  /** Subscription is not in a state that allows mandate creation. */
  MANDATE_SUBSCRIPTION_NOT_READY: 'MANDATE_SUBSCRIPTION_NOT_READY',
  /** A mandate already exists for the subscription. */
  MANDATE_ALREADY_EXISTS: 'MANDATE_ALREADY_EXISTS',
  /** The mandate internal status does not allow the requested action. */
  MANDATE_INVALID_STATUS: 'MANDATE_INVALID_STATUS',
  /** Manual status check requested before the cooldown elapsed. */
  MANDATE_STATUS_CHECK_COOLDOWN: 'MANDATE_STATUS_CHECK_COOLDOWN',
  /** Cancellation cannot be opened or progressed in the current state. */
  MANDATE_CANCELLATION_NOT_ALLOWED: 'MANDATE_CANCELLATION_NOT_ALLOWED',
  /** Bank account is missing, inactive or not usable for mandate setup. */
  MANDATE_BANK_ACCOUNT_INVALID: 'MANDATE_BANK_ACCOUNT_INVALID',
  /** Idempotency key reuse with conflicting request parameters. */
  MANDATE_IDEMPOTENCY_CONFLICT: 'MANDATE_IDEMPOTENCY_CONFLICT',
  /** Provider rejected or failed the mandate operation. */
  MANDATE_PROVIDER_ERROR: 'MANDATE_PROVIDER_ERROR',

  // --- Billing and payments (Phase 4) --------------------------------------
  /** Schedule or attempt status does not allow the requested action. */
  BILLING_INVALID_STATUS: 'BILLING_INVALID_STATUS',
  /** No active mandate is available for billing. */
  BILLING_MANDATE_NOT_ACTIVE: 'BILLING_MANDATE_NOT_ACTIVE',
  /** Authorization link or session is invalid or expired. */
  BILLING_AUTHORIZATION_INVALID: 'BILLING_AUTHORIZATION_INVALID',
  /** OTP resend requested before cooldown elapsed. */
  BILLING_OTP_COOLDOWN: 'BILLING_OTP_COOLDOWN',
  /** OTP resend or attempt limit reached. */
  BILLING_OTP_LIMIT: 'BILLING_OTP_LIMIT',
  /** OTP challenge has expired. */
  BILLING_OTP_EXPIRED: 'BILLING_OTP_EXPIRED',
  /** Submitted OTP is invalid. */
  BILLING_OTP_INVALID: 'BILLING_OTP_INVALID',
  /** Payment has already been posted for this attempt. */
  BILLING_PAYMENT_ALREADY_POSTED: 'BILLING_PAYMENT_ALREADY_POSTED',
  /** Payment provider rejected or failed the operation. */
  BILLING_PROVIDER_ERROR: 'BILLING_PROVIDER_ERROR',
  /** Idempotency key reuse with conflicting parameters. */
  BILLING_IDEMPOTENCY_CONFLICT: 'BILLING_IDEMPOTENCY_CONFLICT',
} as const;

/** Union of every error code the API may return. */
export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

/** Generic messages that never disclose internal state to the caller. */
export const API_ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  VALIDATION_ERROR: 'The submitted data is invalid.',
  BAD_REQUEST: 'The request could not be understood.',
  UNAUTHENTICATED: 'Authentication is required to access this resource.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  METHOD_NOT_ALLOWED: 'This method is not allowed for the requested resource.',
  CONFLICT: 'The request conflicts with the current state of the resource.',
  PAYLOAD_TOO_LARGE: 'The request payload is too large.',
  UNPROCESSABLE_ENTITY: 'The request could not be processed.',
  RATE_LIMITED: 'Too many requests. Please try again later.',
  INTERNAL_ERROR: 'An unexpected error occurred. Please try again later.',
  SERVICE_UNAVAILABLE: 'The service is temporarily unavailable.',

  EMAIL_ALREADY_REGISTERED: 'An account with this email address already exists.',
  INVALID_CREDENTIALS: 'The email address or password is incorrect.',
  EMAIL_NOT_VERIFIED: 'Verify your email address before signing in.',
  ACCOUNT_SUSPENDED: 'This account is suspended. Contact support for assistance.',
  ACCOUNT_DEACTIVATED: 'This account is no longer active.',
  ACCOUNT_LOCKED: 'Too many failed attempts. Try again later.',
  WEAK_PASSWORD: 'The password does not meet the security requirements.',
  INVALID_VERIFICATION_TOKEN: 'This verification link is invalid or has expired.',
  INVALID_RESET_TOKEN: 'This password reset link is invalid or has expired.',
  INVALID_REFRESH_TOKEN: 'Your session has expired. Please sign in again.',

  PROTECTED_SYSTEM_ROLE: 'This role is required by the platform and cannot be changed.',
  LAST_SUPER_ADMIN: 'The last active super administrator cannot be removed.',
  INVALID_UUID: 'The supplied identifier is not valid.',

  APPLICATION_ACTIVE_JOURNEY_EXISTS:
    'You already have an application or financing journey in progress.',
  APPLICATION_DISCOUNT_REVIEW_REQUIRED:
    'This application needs staff review before it can be submitted.',

  MANDATE_SUBSCRIPTION_NOT_READY: 'The contract is not ready for direct debit mandate setup.',
  MANDATE_ALREADY_EXISTS: 'A mandate already exists for this contract.',
  MANDATE_INVALID_STATUS: 'This action is not allowed for the current mandate state.',
  MANDATE_STATUS_CHECK_COOLDOWN:
    'A status check was requested recently. Please wait before trying again.',
  MANDATE_CANCELLATION_NOT_ALLOWED:
    'Mandate cancellation cannot be requested or progressed in the current state.',
  MANDATE_BANK_ACCOUNT_INVALID: 'The selected bank account cannot be used for mandate setup.',
  MANDATE_IDEMPOTENCY_CONFLICT:
    'The idempotency key was already used with different request parameters.',
  MANDATE_PROVIDER_ERROR: 'The payment provider could not complete the mandate operation.',

  BILLING_INVALID_STATUS: 'This action is not allowed for the current billing state.',
  BILLING_MANDATE_NOT_ACTIVE: 'An active direct debit mandate is required for this payment.',
  BILLING_AUTHORIZATION_INVALID:
    'This payment authorization link is invalid, expired or no longer available.',
  BILLING_OTP_COOLDOWN: 'Please wait before requesting another OTP.',
  BILLING_OTP_LIMIT: 'The maximum number of OTP requests or attempts has been reached.',
  BILLING_OTP_EXPIRED: 'The OTP has expired. Request a new one to continue.',
  BILLING_OTP_INVALID: 'The OTP entered is invalid.',
  BILLING_PAYMENT_ALREADY_POSTED: 'This payment has already been posted.',
  BILLING_PROVIDER_ERROR: 'The payment provider could not complete the payment operation.',
  BILLING_IDEMPOTENCY_CONFLICT:
    'The idempotency key was already used with different request parameters.',
};
